import clsx from "clsx";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import RBACTable from "./RBACTable";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import RBACDisclosure from "./RBACDisclosure";
import Toggle from "@/app/components/ui/Toggles";
import { useRouter } from "@/app/hooks/useRouter";
import { useSearchParams } from "react-router-dom";
import Input from "@/app/components/ui/Inputs/Input";
import { RegisterOptions, useForm } from "react-hook-form";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useConfigurationRBACUpdateAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationRBACUpdateAPI";
import { useConfigurationRBACCreateAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationRBACCreateAPI";
import { useConfigurationRBACDetailsAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationRBACDetailsAPI";

export interface IRolesAndPermissionFormFields {
  name: string;
  all_access: boolean;
  modules: string[];
  actions: string[];
}

const ConfigurationRolesOnboard = () => {
  const [isForUpdate, setIsForUpdate] = useState<boolean>(false);
  const [params, _setParams] = useSearchParams();
  const navigate = useNavigate();
  const [accessView, setAccessView] = useState<"TABLE" | "DISCLOSURE">("DISCLOSURE");
  const { getRouteKey } = useRouter();
  const { forAllAcceptWithOutStartWithDotAndSpace } = useFormValidations();
  const createRolesAndPermissions = useConfigurationRBACCreateAPI();
  const updateRolesAndPermissions = useConfigurationRBACUpdateAPI();
  const { roleDetails, execute: fetchRolesAndPermissionDetails } = useConfigurationRBACDetailsAPI();

  const formHook = useForm<IRolesAndPermissionFormFields>({
    mode: "onChange",
    defaultValues: {
      actions: [],
      modules: [],
    },
  });

  const handleCancel = () => {
    formHook.reset();
    navigate(getRouteKey("ROLES_PERMISSION", "url"));
  };

  const onInvalidSubmit = (_errors) => {};

  const onSubmit = (data: IRolesAndPermissionFormFields) => {
    formHook.trigger().then((isValid) => {
      if (isValid) {
        if (isForUpdate) {
          updateRolesAndPermissions({
            role_id: params.get("role_id"),
            role_permission: {
              actions: data.actions,
              modules: data.modules,
            },
          }).then(() => {
            navigate(getRouteKey("ROLES_PERMISSION", "url"));
          });
        } else {
          createRolesAndPermissions({
            role_name: data.name,
            role_permission: {
              actions: data.actions,
              modules: data.modules,
            },
          });
        }
      }
    });
  };

  const handleAddModuleAndActions = (modules: string[], actions: string[]) => {
    formHook.setValue("actions", actions);
    formHook.setValue("modules", modules);
  };

  useEffect(() => {
    if (params.get("role_id")) {
      fetchRolesAndPermissionDetails(params.get("role_id")).then(() => {
        setIsForUpdate(true);
      });
    }
  }, [params.get("role_id")]);

  useEffect(() => {
    if (roleDetails) {
      formHook.setValue("name", roleDetails.role_name);
      formHook.setValue("actions", roleDetails.role_permission.actions);
      formHook.setValue("modules", roleDetails.role_permission.modules);
    }
  }, [roleDetails]);

  return (
    <div className="bg-white py-1 rounded-md">
      <div className="mt-2 items-center">
        <div className="flex-col flex">
          <div className="px-3 justify-between items-center flex">
            <div className="text-black text-base font-semibold leading-tight">Staff Roles</div>
          </div>
        </div>
      </div>
      <div className="mx-3">
        <form onSubmit={formHook.handleSubmit(onSubmit, onInvalidSubmit)}>
          <input
            hidden
            type="text"
            {...formHook.register("actions", {
              required: true,
            })}
          />
          <div className="justify-start items-baseline flex mt-5 border-b pb-3">
            <div className="h-[38px] p-2.5 justify-start w-[200px] items-center gap-1 flex mb-5">
              <span className="text-black text-xs font-normal">Role Name</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <Input
              className="h-9 w-[300px] rounded-md border-slate-400"
              placeholder="Enter Role Name"
              disabled={isForUpdate}
              register={formHook.register("name", {
                required: true,
                ...forAllAcceptWithOutStartWithDotAndSpace.validations,
              } as RegisterOptions<IRolesAndPermissionFormFields, "name">)}
              fieldError={formHook.formState.errors.name}
              errorMessages={[
                { message: "name is required", type: "required" },
                forAllAcceptWithOutStartWithDotAndSpace.errors,
              ]}
            />
          </div>
          <div className="flex justify-between items-center">
            <div className="mt-5 mx-3">
              <span className="text-sm font-semibold">Permissions</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <div className="flex justify-end items-center mt-5">
              <span className="isolate inline-flex rounded-md shadow-sm">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAccessView("DISCLOSURE");
                  }}
                  type="button"
                  className={clsx(
                    "relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-semibold ring-1 ring-inset focus:z-10",
                    accessView === "DISCLOSURE"
                      ? "bg-blue-100 text-blue-900 ring-blue-300 hover:bg-blue-50"
                      : "bg-white text-gray-900 ring-gray-300 hover:bg-gray-50",
                  )}
                >
                  Easy
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAccessView("TABLE");
                  }}
                  type="button"
                  className={clsx(
                    "relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-semibold ring-1 ring-inset focus:z-10",
                    accessView === "TABLE"
                      ? "bg-blue-100 text-blue-900 ring-blue-300 hover:bg-blue-50"
                      : "bg-white text-gray-900 ring-gray-300 hover:bg-gray-50",
                  )}
                >
                  Advance
                </button>
              </span>
            </div>
          </div>
          {accessView === "TABLE" && <RBACTable />}
          {accessView === "DISCLOSURE" && (
            <RBACDisclosure
              defaultSelectedModules={formHook.watch("modules")}
              defaultSelectedActions={formHook.watch("actions")}
              allAccess={formHook.watch("all_access")}
              onChange={(modules, actions) => handleAddModuleAndActions(modules, actions)}
            />
          )}
          {formHook.formState.errors.actions && (
            <span className="text-red-500 text-xs pl-2">Please select atleast one permissions.</span>
          )}
          <div className="justify-start items-center flex px-1 mt-5 mb-16">
            <div className="h-[38px] p-2.5 justify-start w-[150px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Allow All Access</span>
            </div>
            <Toggle
              checked={formHook?.watch("all_access") || false}
              onToggle={(state) => {
                formHook?.setValue("all_access", state);
              }}
            />
          </div>
          <div className="right-0 bottom-0 p-3 justify-end items-center gap-4 flex w-full border-t-2 fixed bg-white">
            <button className={SECONDARY_BUTTON} onClick={handleCancel}>
              Cancel
            </button>
            <input
              type="submit"
              className={`${PRIMARY_BUTTON} cursor-pointer`}
              value={isForUpdate ? "Update" : "Save"}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConfigurationRolesOnboard;
